import React, { useState, useEffect, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";

// Material UI Core
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { Divider, Tab, Tabs, Typography } from "@mui/material";

// Custom Components
import { GridContainer, GridItem } from "@/ui/Grid";
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import OperationsContext, { OperationsContextType } from "@/components/OperationsContext/OperationsContext";

import dataController from "@/lib/dataController";
import { DCRecord } from "@/@types/lib/dataController";
import AppPage from "@/ui/AppPage/AppPage";
import DialogContext from "@/ui/DialogContext/DialogContext";

import RadniSastanciObjectTable from "./RadniSastanciObjectTable"
import RadniSastanciKomunalneObvezeTable from "./RadniSastanciKomunalneObvezeTable";
import RadniSastanciLocationTable from "./RadniSastanciLocationTable";
import RadniSastanciMap from "./RadniSastanciMap";
import RadniSastanciCommentBlock from "./RadniSastanciCommentBlock";
import KomunalneDialog from "./KomunalneDialog";

import modelObjekti from "@/models/objekti";
import modelKomunalne from "@/models/komunalne_obveze";
import modelLokacije from "@/models/lokacije";

import { SnackbarContextType } from "@/@types/ui/SnackbarContext";
import { DialogContextType } from "@/@types/ui/DialogContext";
import modelStatusi from "@/models/statusi";
import UserContext from "@/components/UserContext/UserContext";
import { Permissions } from "@/lib/permissions";

const RadniSastanciPage = React.memo(() => {
//#region Variable Definitions
  const userContext = useContext(UserContext);

  const MAP_ID = 82;

  const dialogContext = useContext<DialogContextType>(DialogContext);
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;
  const operationsContext = useContext(OperationsContext) as OperationsContextType;

  const { t } = useTranslation();
  const dontRefresh = useRef<boolean>(false);
  const selectedProject = operationsContext.selectedProject;

  // Table rows - Objekti, lokacije, komunalne obveze 
  const [records, setRecords] = useState<Array<DCRecord>>([]);
  const [recordsLokacije, setRecordsLokacije] = useState<DCRecord[]>([]);
  const [recordsKomunalne, setRecordsKomunalne] = useState<DCRecord[]>([]);

  // Selected rows for each table
  const [selectedRecord, setSelectedRecord] = useState<DCRecord | null>(null);
  const [selectedLokacija, setSelectedLokacija] = useState<DCRecord | null>(null);
  const [selectedKomunalna, setSelectedKomunalna] = useState<DCRecord | null>(null);

  // Selected row in current state, for map selection
  const [selectedRecordFinal, setSelectedRecordFinal] = useState<DCRecord | null>(null);
  // Current table
  const [selectedState, setSelectedState] = useState<number>(0);

  // Old image values
  const [refreshState, setRefreshState] = useState<boolean>(false)

  const dcObjekti = new dataController(modelObjekti, 'operations/objekti/');
  const dcKomunalne = new dataController(modelKomunalne, 'operations/projekti/' + Number(operationsContext.selectedProject?.id).toString() + '/komunalne-obveze');
  const dcLokacije = new dataController(modelLokacije, 'operations/projekti/' + Number(operationsContext.selectedProject?.id).toString() + '/lokacije');

  const statusFields = useRef<DCRecord[] | null>(null);
  const lokacijeStatusFields = useRef<DCRecord[] | null>(null);
  const objektiStatusFields = useRef<DCRecord[] | null>(null);

//#endregion
//#region Record Selection

  const handleRecordSelect = (id: number, record: DCRecord | null) => {
    setSelectedKomunalna(null);
    setSelectedLokacija(null);

    if(selectedState !== 0) {
      setSelectedState(0);
    }
    if (selectedRecord !== null && id === selectedRecord.id) {
      //@ts-ignore
      setSelectedRecord(null);
      return;
    } else if (id !== -1 && id !== null) {
      const newRecord = records.find(element => element.id === id);
      if(newRecord) {
        setSelectedRecord(newRecord);
      } else {
        setSelectedRecord(null);
      }
    } else {
      //@ts-ignore
      setSelectedRecord(null);
    }

    dcObjekti.GetDataSingle(id).then((resp: any) => {
      if (resp.success && resp.data) {
        setSelectedRecord(resp.data);
      }
    }).catch().finally(() => {
    });
  }

  const handleLokacijeSelect = (id: number, record: DCRecord | null) => {
    setSelectedKomunalna(null);
    setSelectedRecord(null);

    if(selectedState !== 1) {
      setSelectedState(1);
    }

    if (selectedLokacija !== null && id === selectedLokacija.id) {
      //@ts-ignore
      setSelectedLokacija(null);
    } else if (id !== -1 && id !== null) {
      const newLocation = recordsLokacije.find(element => element.id === id);
      if(newLocation) {
        setSelectedLokacija(newLocation);
      } else {
        setSelectedLokacija(null);
      }
    } else {
      //@ts-ignore
      setSelectedLokacija(null);
    }
  }; 

  const handleKomunalneSelect = (id: number, record: DCRecord | null) => {
    setSelectedRecord(null);
    setSelectedLokacija(null);

    if(selectedState !== 2) {
      setSelectedState(2);
    }
    if (selectedKomunalna !== null && id === selectedKomunalna.id) {
      //@ts-ignore
      setSelectedKomunalna(null);
    } else if (id !== -1 && id !== null) {
      const newKomunalna = recordsKomunalne.find(element => element.id === id);
      if(newKomunalna) {
        setSelectedKomunalna(newKomunalna);
      } else {
        setSelectedKomunalna(null);
      }
    } else {
      //@ts-ignore
      setSelectedKomunalna(null);
    }
  }

  const refreshRecords = () => {
    const { selectedProject } = operationsContext;
    if (selectedProject) {
      const url = `operations/projekti/${selectedProject.id}/objekti/`
      dcObjekti.GetData(url)
        .then((resp) => {
          if (resp.success && Array.isArray(resp.data)) {
            setRecords(resp.data)

            if (selectedRecord && selectedRecord.id) {
              const newValueForSelectedRecord = resp.data.find(x => x.id === selectedRecord.id);
              if (newValueForSelectedRecord) {
                const { broj_katova, broj_dijelova, pre_pb } = newValueForSelectedRecord;
                //@ts-ignore
                OP3_CALC.setValues(broj_katova, broj_dijelova, pre_pb);
                setSelectedRecord(newValueForSelectedRecord);
              } else {
                //@ts-ignore
                OP3_CALC.resetValues();
                setSelectedRecord(null);
              }
            }

            if (resp.data.length === 0) {
              snackbarContext.showNotification("messages.nodata", "warning", true);
            }
          };
        })
        .catch((resp) => {
          console.log(resp);
        });
    } else {
      setRecords([]);
    }
  };

  const refreshKomunalneRecords = (select?: boolean) => {
    if (!selectedProject) {
      setRecordsKomunalne([]);
      return
    }

    const url = `operations/projekti/${selectedProject.id}/komunalne-obveze`
    dcKomunalne.GetData(url).then((resp) => {
      if (resp.success) {
        setRecordsKomunalne(resp.data as Array<DCRecord>);
      };
    }).catch((resp) => {
      console.log(resp);
    });

    if(select) {
      setSelectedKomunalna(null);
    }
  };

  const refreshLokacijeRecords = () => {
    if (!selectedProject) {
      setRecordsLokacije([]);
      return;
    }

    // console.log(selectedProject.id);
    const url = `operations/projekti/${selectedProject.id}/lokacije`
    dcLokacije.GetData(url).then((resp) => {
      if (resp.success) {
        setRecordsLokacije(resp.data as Array<DCRecord>);
      };
    }).catch((resp) => {
      console.log(resp);
    });

    setSelectedLokacija(null);
  };

//#endregion
//#region Helpers

  const clearSelections = () => {
    //@ts-ignore
    setSelectedRecord(prev => prev === null ? prev : null);
  }

  const handleTableStateChange = (event: any, newState: number) => {
    setSelectedState(newState);
  }


//#endregion
//#region useEffect

  useEffect(() => {
    let dcStatusi = new dataController(modelStatusi, 'operations/statusi-ko');
    dcStatusi.GetData().then(resp => {
        if(resp.success && resp.data && resp.data !== null && Array.isArray(resp.data)) {
            statusFields.current = resp.data.slice();
        }
    }).catch(resp => console.log(resp));

    dcStatusi = new dataController(modelStatusi, 'operations/statusi-lokacije');
    dcStatusi.GetData().then(resp => {
        if(resp.success && resp.data && resp.data !== null && Array.isArray(resp.data)) {
            lokacijeStatusFields.current = resp.data.slice();
        }
    }).catch(resp => console.log(resp));

    dcStatusi = new dataController(modelStatusi, 'operations/statusi-objekti');
    dcStatusi.GetData().then(resp => {
        if(resp.success && resp.data && resp.data !== null && Array.isArray(resp.data)) {
            objektiStatusFields.current = resp.data.slice();
        }
    }).catch(resp => console.log(resp));
  }, []);

  useEffect(() => {
    if(selectedState === 0) {
      setSelectedRecordFinal(selectedRecord);
    } else if(selectedState === 1) {
      setSelectedRecordFinal(selectedLokacija);
    } else if (selectedState === 2) {
      setSelectedRecordFinal(selectedKomunalna);
    }
  }, [selectedRecord, selectedLokacija, selectedKomunalna]);

  useEffect(() => {
    if(operationsContext.selectedProject !== null) {
      refreshLokacijeRecords();
    }
  }, [selectedProject]);

  useEffect(() => {
    if(operationsContext.selectedProject !== null) {
      refreshKomunalneRecords();
    }
  }, [selectedProject]);

  useEffect(() => {
    if (operationsContext.selectedProject === null && selectedRecord !== null && operationsContext.projectItems.length !== 0) {
      const { projekt_id } = selectedRecord;
      if (projekt_id) {
        operationsContext.selectProject(projekt_id as number);
      }
    }
  }, [selectedRecord, operationsContext.projectItems]);

  useEffect(() => {
    if (operationsContext.selectedFishnet === null && selectedRecord !== null && operationsContext.fishnetItems.length !== 0) {
      const { fishnet_id } = selectedRecord;
      if (fishnet_id) {
        dontRefresh.current = true;
        operationsContext.selectFishnet(fishnet_id as number);
      }
    }
  }, [selectedRecord, operationsContext.fishnetItems]);

  useEffect(() => {
    if (operationsContext.selectedProject) {
      if (dontRefresh.current) {
        dontRefresh.current = false;
      } else {
        refreshRecords();
      }
    } else {
      setRecords([]);
      clearSelections();
    }
  }, [operationsContext.selectedProject]);

  useEffect(() => {
    if (!operationsContext.selectedProject) {
      clearSelections();
    }
  }, [operationsContext.selectedProject]);

//#endregion
//#region Saving comments and new forms

  const onSaveComment = (text: string, selected?: DCRecord) => {
    // If comment was changed in the meantime, don't allow update

    if(selectedState === 0) {      
      if(selected && selected !== null && selected.id && selected.id !== null) {
        // comment update sent from KatoviDialog

        dcObjekti.GetDataSingle(Number(selected.id?.toString()), 'operations/objekti/' + selected.id?.toString())
          .then((resp: any) => {
            if(resp.data.komentar !== selected.komentar) {
              // L_TODO: Replace with dialog
              snackbarContext.showNotification(
                '"' + resp.data.komentar + '" (' + t('notifications.database_changed_alert') + ' - ' + t('notifications.database_changed_note') + ')'
                , 'warning', true);
              selected.komentar = resp.data.komentar;
            } else {
              const newRecord = {
                id: selected.id,
                projekt_id: selected.projekt_id,
                pregledano: selected.pregledano,
                gl_obj: selected.gl_obj,
                foto_1: selected.foto_1,
                foto_2: selected.foto_2,
                foto_1_extent: selected.foto_1_extent,
                foto_2_extent: selected.foto_2_extent,
                napomena: selected.napomena,
                komentar: text
              };
              dcObjekti.UpdateRecord(Number(newRecord.id?.toString()), newRecord, 'operations/objekti')
                .then((resp: any) => {
                  if (resp.success) {
                    snackbarContext.showNotification(t('notifications.update'), 'alert', false);
                  }
                }).catch((resp) => {
                  snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
                  console.log(resp);
                });
            }
          }).catch((resp: any) => {
            snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
            console.log(resp);
          });

          return;
      }

      if(selectedRecord && selectedRecord !== null) {
        const newRecord = {
          id: selectedRecord.id,
          projekt_id: selectedRecord.projekt_id,
          pregledano: selectedRecord.pregledano,
          gl_obj: selectedRecord.gl_obj,
          foto_1: selectedRecord.foto_1,
          foto_2: selectedRecord.foto_2,
          foto_1_extent: selectedRecord.foto_1_extent,
          foto_2_extent: selectedRecord.foto_2_extent,
          napomena: selectedRecord.napomena,
          komentar: text
        }

        dcObjekti.GetDataSingle(Number(selectedRecord.id?.toString()), 'operations/objekti/' + selectedRecord.id?.toString())
          .then((resp: any) => {
            if(resp.data.komentar !== selectedRecord.komentar) {
              // L_TODO: Replace with dialog
              snackbarContext.showNotification(
                '"' + resp.data.komentar + '" (' + t('notifications.database_changed_alert') + ' - ' + t('notifications.database_changed_note') + ')'
                , 'warning', true);
              selectedRecord.komentar = resp.data.komentar;
            } else {
              dcObjekti.UpdateRecord(Number(selectedRecord?.id?.toString()), newRecord, 'operations/objekti')
                .then((resp: any) => {
                  if (resp.success) {
                    selectedRecord.komentar = text;
                    setRecords(records.map((record: any) => {
                      if(record.id === selectedRecord.id) {
                        record.komentar=text;
                        return record;
                      } else {
                        return record;
                      }
                    }));
                    snackbarContext.showNotification(t('notifications.update'), 'alert', false);
                  }
                }).catch((resp) => {
                  snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
                  console.log(resp);
                });
            }
          }).catch((resp: any) => {
            snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
            console.log(resp);
          });
      } else {
        snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
      }
    } else if(selectedState === 1) {
      // console.log('New Comment for location ' + selectedLokacija?.id + ': ' + text);
      if(selectedLokacija && selectedLokacija !== null) {
        const newLokacija = {
          id: selectedLokacija.id, 
          kb: selectedLokacija.kb,
          kc: selectedLokacija.kc,
          ul_ime: selectedLokacija.ul_ime,
          na_ime: selectedLokacija.na_ime,
          na_mb: selectedLokacija.na_mb,
          ko_ime: selectedLokacija.ko_ime,
          zona: selectedLokacija.zona,
          ko_mb: selectedLokacija.ko_mb,
          lokacija_status_id: selectedLokacija.lokacija_status_id,
          kn_koef: selectedLokacija.kn_koef,
          ul_jid: selectedLokacija.ul_jid,
          geom: selectedLokacija.wkt,
          komentar: text
        }

        dcLokacije.GetDataSingle(Number(selectedLokacija?.id?.toString()), 
          'operations/projekti/' + selectedProject?.id + '/lokacije/' + selectedLokacija?.id?.toString())
          .then((resp: any) => {
            if(resp.data.komentar !== selectedLokacija.komentar) {
              // L_TODO: Replace with dialog
              snackbarContext.showNotification(
                '"' + resp.data.komentar + '" (' + t('notifications.database_changed_alert') + ' - ' + t('notifications.database_changed_note') + ')'
                , 'warning', true);
              selectedLokacija.komentar = resp.data.komentar;
            } else {
              const currPath = "operations/projekti/" + selectedProject?.id + "/lokacije"
              dcLokacije.UpdateRecord(Number(selectedLokacija?.id?.toString()), newLokacija, currPath)
                .then((resp: any) => {
                  if (resp.success) {
                    // This is all so comments get immediately updated
                    selectedLokacija.komentar = text;
                    setRecordsLokacije(recordsLokacije.map((record: any) => {
                      if(record.id === selectedLokacija.id) {
                        return selectedLokacija;
                      } else {
                        return record;
                      }
                    }));

                    snackbarContext.showNotification(t('notifications.update'), 'alert', false);
                  }
                }).catch((resp) => {
                  snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
                  console.log(resp);
                });
            }
          }).catch((resp: any) => {
            snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
            console.log(resp);
          });
      } else {
        snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
      }
    } else if(selectedState === 2) {
      if(selectedKomunalna && selectedKomunalna !== null) {
        const newKomunalna: DCRecord = {
          id: selectedKomunalna.id,
          adresa_obveznika: selectedKomunalna.adresa_obveznika,
          lokacija_id: selectedKomunalna.lokacija_id,
          namjena_id: selectedKomunalna.namjena_id,
          naselje_obveznika: selectedKomunalna.naselje_obveznika,
          obveznik: selectedKomunalna.obveznik,
          oib: selectedKomunalna.oib,
          projekt_id: selectedKomunalna.projekt_id,
          status_obv: selectedKomunalna.status_obv,
          ulica_obveznika: selectedKomunalna.ulica_obveznika,
          zapisnik: selectedKomunalna.zapisnik,
          komentar: text
        }

        dcKomunalne.GetDataSingle(Number(selectedKomunalna?.id?.toString()), 
          'operations/projekti/' + selectedProject?.id + '/komunalne-obveze/' + selectedKomunalna?.id?.toString())
          .then((resp: any) => {
            if(resp.data.komentar !== selectedKomunalna.komentar) {
              // L_TODO: Replace with dialog
              snackbarContext.showNotification(
              '"' + resp.data.komentar + '" (' + t('notifications.database_changed_alert') + ' - ' + t('notifications.database_changed_note') + ')'
              , 'warning', true);
              selectedKomunalna.komentar = resp.data.komentar;
            } else {
              const currPath = "operations/projekti/" + selectedProject?.id + "/komunalne-obveze"
              dcKomunalne.UpdateRecord(Number(selectedKomunalna?.id?.toString()), newKomunalna, currPath)
                .then((resp: any) => {
                  if (resp.success) {
                    selectedKomunalna.komentar = text;
                    setRecordsKomunalne(recordsKomunalne.map((record: any) => {
                      if(record.id === selectedKomunalna.id) {
                        return selectedKomunalna;
                      } else {
                        return record;
                      }
                    }));

                    snackbarContext.showNotification(t('notifications.update'), 'alert', false);
                  }
                }).catch((resp) => {
                  snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
                  console.log(resp);
                });
            }
          }).catch((resp: any) => {
            snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
            console.log(resp);
          });
      } else {
        snackbarContext.showNotification(t('notifications.failed'), 'warning', false);
      }
    }
  }

  const handleAddKomunalne = () => {
    dialogContext.showDialog(KomunalneDialog, {
      dc: dcKomunalne,
      mode: "insert",
      form: "insert",
      onClose: handleDialogClose,
      selectedRecord: selectedKomunalna,
      defaultValues:{
        lokacija_id: selectedLokacija?.id,
        status_obv: 2110
      }
    });
  };

  const handleDialogClose = (result: { dataChanged: boolean; action: string }) => {
    if (result.dataChanged) {
        refreshKomunalneRecords(true);
    }
  };

  const handleKatoviDialogClose = (result: { dataChanged: boolean; action: string }) => {
    setRefreshState(true);
  };

  const handleGoToObjectAnalysis = () => {
    if (selectedRecord?.id) {
      window.open(`/object-analysis?object_id=${selectedRecord.id}`, "_blank");
    }
  };

  useEffect(() => {
    if(refreshState === true) {
      setRefreshState(false);
      refreshRecords();
    }
  }, [refreshState]);
  
//#endregion
//#region Return

  return (
    <AppPage needPermission={[Permissions.Administration]}>
      <GridContainer sx={{height: "calc(100% - 22px)"}}>

        <GridItem xs={6}>
          <RadniSastanciMap
            records={records}
            onObjektSelect={handleRecordSelect}
            selectedObjekt={selectedRecordFinal}
            lokacije={recordsLokacije}
            onLokacijeSelect={handleLokacijeSelect}
            selectedLokacija={selectedLokacija}
            imagesBasicInfo={[]}
            selectedImageId={null}
            mapId={MAP_ID}
            refreshLocations={refreshLokacijeRecords}
            selectedObjektFor3D={selectedRecord}
            currentTab={selectedState === 0 ? true : false}
          />
        </GridItem>


        <GridItem xs={6} sx={{height: "100%"}}>
          <GridItem xs={12}>
            <GridContainer>

                <GridItem xs={10}>
                  <Tabs value={selectedState} onChange={handleTableStateChange}>
                    <Tab label={t("titles.objekti")}></Tab>
                    <Tab label={t("titles.lokacije")}></Tab>
                    <Tab label={t("titles.komunalne_obveze")}></Tab>
                  </Tabs>
                </GridItem> 

                <GridItem xs={12} sx={{height: "60vh"}}>
                  {selectedState === 0 ? 
                    <GridItem xs={12} sx={{height: "100%"}}>
                      <Box m={0} sx={{height: "100%"}}>
                        <RadniSastanciObjectTable
                          records={records}
                          onRowSelect={handleRecordSelect}
                          model={modelObjekti}
                          titleToken={"titles.objekti"}
                          rowSelectedCustom={selectedRecord ? selectedRecord.id as number : undefined}
                          lokacijaId={selectedRecord !== null ? Number(selectedRecord?.id) : undefined}
                          selectedLokacija={selectedLokacija}
                          onSave={onSaveComment}
                          onCloseDialog={handleKatoviDialogClose}
                          statusFields={objektiStatusFields !== null ? objektiStatusFields.current as DCRecord[] : undefined}
                          refreshRecords={refreshRecords}
                        />
                      </Box>
                    </GridItem> 
                    : null
                  }

                  {selectedState === 1 ? 
                    <GridItem xs={12} sx={{height: "100%"}}>
                      <Box m={0} sx={{height: "100%"}}>
                        <RadniSastanciLocationTable 
                          dc={dcLokacije}
                          records={recordsLokacije}
                          onRowSelect={handleLokacijeSelect}
                          selectedProject={selectedProject}
                          rowSelectedCustom={selectedLokacija ? selectedLokacija.id as number : undefined}
                          statusFields={lokacijeStatusFields !== null ? lokacijeStatusFields.current as DCRecord[] : undefined}
                          refreshRecords={refreshLokacijeRecords}
                        />
                      </Box>
                    </GridItem> :
                    null
                  }

                  {selectedState === 2 ? 
                    <GridItem xs={12} sx={{height: "100%"}}>
                      <RadniSastanciKomunalneObvezeTable
                        dc={dcKomunalne}
                        records={recordsKomunalne}
                        handleOpenExportDialog={() => {return null;}}
                        onRowSelect={handleKomunalneSelect}
                        selectedProject={selectedProject}
                        selectedLokacija={selectedLokacija}
                        handleAddKomunalne={handleAddKomunalne}
                        rowSelectedCustom={selectedKomunalna ? selectedKomunalna.id as number : undefined}
                        statusFields={statusFields !== null ? statusFields.current as DCRecord[] : undefined}
                        refreshRecords={refreshKomunalneRecords}
                      />
                    </GridItem> :
                    null
                  }
                </GridItem>

            </GridContainer>
          </GridItem>
            
          <br></br>
          <Divider></Divider>

          {userContext && userContext.permissionIds.indexOf(800) !== -1 ?
            (selectedState === 0 && selectedRecord) ? 
              (
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <Typography>{t(`titles.objekt`)} {selectedRecord?.id}</Typography>
                  <Button
                    size="small"
                    sx={{ width: "max-content", mb: "4px" }}
                    variant="contained"
                    onClick={handleGoToObjectAnalysis}
                  >
                    {t("buttons.object_analysis")}
                  </Button>
                  <RadniSastanciCommentBlock
                    selectedRecord={selectedRecord}
                    onSave={onSaveComment}
                  />
                </Box>
              ) : 
                (selectedState === 1 && selectedLokacija) ? 
                (
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <Typography>{t(`titles.lokacija`)} {selectedLokacija?.id}</Typography>
                  <RadniSastanciCommentBlock
                    selectedRecord={selectedLokacija}
                    onSave={onSaveComment}
                  />
                </Box>
                ) : 
                  (selectedState === 2 && selectedKomunalna) ? 
                  (
                <Box display={"flex"} flexDirection={"column"} gap={1}>
                  <Typography>{t(`titles.komunalna_obveza`)} {selectedKomunalna?.id}</Typography>
                  <RadniSastanciCommentBlock
                    selectedRecord={selectedKomunalna}
                    onSave={onSaveComment}
                  />
                </Box>
                  ) : null
          : null}
        </GridItem>
        
      </GridContainer>
    </AppPage>
  );
});

export default RadniSastanciPage;
